import { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  CardHeader,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconCalculator, IconHelp } from "@tabler/icons-react";
import MoedaText from "../../../util/ui/inputs/moedaText";
import { Skeleton } from "@mui/material";
import { useUsuario } from "../../../../providers/usuarioProvider";
import { Moment } from "moment";
import Abatimentos from "../../../../models/abatimentos";
import Darf from "../../../../models/darf";
import { onSnapshot } from "firebase/firestore";
import ImpostoDb from "../../../../db/impostoDb";
import FelixCard from "../../../util/ui/felixCard";

type Props = {
  mes: Moment;
  darf?: Darf;
};

export default function PrejuizoAcumulado({ darf, mes }: Props) {
  const { visualizado } = useUsuario();
  const [acumulado, setAcumulado] = useState<Abatimentos | undefined>();

  useEffect(() => {
    if (mes && visualizado && !!!darf) {
      return onSnapshot(
        new ImpostoDb().doc({
          usuarioId: visualizado,
          impostoId: mes.clone().subtract(1, "month").format("YYYY-MM"),
        }),
        (snapshot) => {
          setAcumulado(snapshot.data()?.abatimentos);
        }
      );
    }
  }, [visualizado, darf, mes]);

  return (
    <FelixCard>
      <CardHeader
        title={darf ? "Prejuízo compensado" : "Prejuízo acumulado"}
        avatar={<IconCalculator size={16} />}
        action={
          <Box display="flex" flexDirection="row" alignItems="center">
            <Tooltip title="É possível compensar prejuízos que foram acumulados em meses anteriores.">
              <Box sx={{ color: "text.secondary" }}>
                <IconHelp size={16} />
              </Box>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography
            color="text.primary"
            sx={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: 28,
              letterSpacing: "-0.02em",
              paddingBottom: 2,
            }}
          >
            {darf || acumulado ? (
              <MoedaText
                value={darf?.prejuizoCompensado ?? acumulado?.prejuizo}
              />
            ) : (
              <Skeleton variant="text" width={40} animation={false} />
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="flex-end">
          <Box
            display="flex"
            flexShrink={1}
            width="100%"
            flexDirection="column"
            alignItems="center"
          >
            <Typography
              variant="dataMono"
              color="text.primary"
              sx={{ fontWeight: 500 }}
            >
              {darf || acumulado ? (
                <MoedaText
                  value={
                    darf?.operacoesComuns.prejuizoCompensado ??
                    acumulado?.operacoesComuns.prejuizo
                  }
                />
              ) : (
                <Skeleton variant="text" width={40} animation={false} />
              )}
            </Typography>
            <Typography variant="labelMono" color="text.secondary" mt={0.5}>
              Comuns
            </Typography>
          </Box>
          <Divider
            sx={{ m: 1 }}
            orientation="vertical"
            variant="middle"
            flexItem
          />
          <Box
            display="flex"
            flexShrink={1}
            width="100%"
            flexDirection="column"
            alignItems="center"
          >
            <Typography
              variant="dataMono"
              color="text.primary"
              sx={{ fontWeight: 500 }}
            >
              {darf || acumulado ? (
                <MoedaText
                  value={
                    darf?.operacoesDaytrade.prejuizoCompensado ??
                    acumulado?.operacoesDaytrade.prejuizo
                  }
                />
              ) : (
                <Skeleton variant="text" width={40} animation={false} />
              )}
            </Typography>
            <Typography variant="labelMono" color="text.secondary" mt={0.5}>
              Daytrade
            </Typography>
          </Box>
          <Divider
            sx={{ m: 1 }}
            orientation="vertical"
            variant="middle"
            flexItem
          />
          <Box
            display="flex"
            flexShrink={1}
            width="100%"
            flexDirection="column"
            alignItems="center"
          >
            <Typography
              variant="dataMono"
              color="text.primary"
              sx={{ fontWeight: 500 }}
            >
              {darf || acumulado ? (
                <MoedaText
                  value={
                    darf?.operacoesFii.prejuizoCompensado ??
                    acumulado?.operacoesFii.prejuizo
                  }
                />
              ) : (
                <Skeleton variant="text" width={40} animation={false} />
              )}
            </Typography>
            <Typography variant="labelMono" color="text.secondary" mt={0.5}>
              FII/Fiagro
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </FelixCard>
  );
}
